import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import {
  RefreshCw, Flame, Activity, AlertCircle, TrendingDown, BarChart3,
} from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';

/**
 * M2 Steam Dashboard — generation, consumption, boiler-efficiency proxy,
 * SEU trend (steam tonnes per kL product). Reads /api/oim/utilities/steam.
 */

const PERIOD_OPTIONS = [
  { id: '7d',   label: 'Last 7 days' },
  { id: '30d',  label: 'Last 30 days' },
  { id: '90d',  label: 'Last 90 days' },
  { id: '180d', label: 'Last 180 days' },
];

const fmt = (n, d = 2) => n == null || isNaN(n) ? '—'
  : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtCompact = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(2) + 'K';
  return v.toFixed(0);
};

export default function OimSteamPanel({ siteId }) {
  const { showError } = useAlert();
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ site_id: siteId, period });
      const res = await axios.get(`${API}/oim/utilities/steam?${params}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
      showError(e.response?.data?.detail || 'Failed to load steam dashboard');
    } finally { setLoading(false); }
  }, [siteId, period, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!siteId) return (
    <div className="p-12 text-center text-gray-500">
      <AlertCircle className="w-10 h-10 mx-auto text-gray-300 mb-2"/>
      Select a site to view steam dashboard.
    </div>
  );

  const k = data?.kpis || {};

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Flame className="w-5 h-5 text-amber-600"/> Steam Dashboard
          </h2>
          <div className="text-xs text-gray-500">Generation, consumption, boiler-efficiency proxy</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="px-2 py-1.5 border rounded text-sm">
            {PERIOD_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <button onClick={fetchData} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/>
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Steam Generation" value={`${fmt(k.steam_generation_tonnes, 1)} T`} sub={`${fmtCompact(k.steam_kwh_eq)} kWh-eq`} accent="amber" loading={loading}/>
        <Kpi
          label="Steam Consumption"
          value={`${fmt(k.steam_consumption_tonnes, 1)} T`}
          sub={k.has_consumption_meters
            ? 'Sum of process-inlet meters (steam_consumed)'
            : 'No process-inlet meters yet — defaulting to generation'}
          accent="amber"
          loading={loading}
        />
        <Kpi
          label="Boiler Efficiency"
          value={k.boiler_efficiency_pct != null ? `${fmt(k.boiler_efficiency_pct, 1)}%` : '—'}
          sub={k.boiler_efficiency_warn
            ? '> 100% — likely missing boiler-fuel rows or steam meter overreading. Verify all fuel meters are uploaded and check feedwater return temp.'
            : 'steam_net_kWh ÷ fuel_kWh (proxy · 650 kWh/tonne net heat)'}
          accent={k.boiler_efficiency_warn ? 'rose' : 'emerald'}
          loading={loading}
        />
        <Kpi label="Steam SEU" value={k.steam_seu != null ? `${fmt(k.steam_seu, 3)} T/${k.production_unit || ''}` : '—'} sub="Steam per unit production" accent="blue" loading={loading}/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-6">
        <Kpi
          label="Steam Losses"
          value={k.steam_losses_tonnes != null
            ? `${fmt(k.steam_losses_tonnes, 1)} T${k.steam_losses_pct != null ? ` (${fmt(k.steam_losses_pct, 1)}%)` : ''}`
            : '—'}
          sub={k.has_consumption_meters
            ? 'Generation − sum of process-inlet meters'
            : "Upload energy CSV with energy_type='steam_generated' (boiler outlet) and 'steam_consumed' (per process line) to populate"}
          accent={k.steam_losses_tonnes != null ? 'amber' : 'gray'}
          loading={loading}
        />
        <Kpi
          label="Condensate Recovery"
          value={k.condensate_recovery_pct != null ? `${fmt(k.condensate_recovery_pct, 1)}%` : '—'}
          sub={k.has_condensate_meter
            ? `${fmtCompact(k.condensate_returned_kl)} kL returned to feedwater tank`
            : "Upload water CSV with water_type='condensate' (feedwater-tank return meter) to populate"}
          accent={k.condensate_recovery_pct != null ? 'emerald' : 'gray'}
          loading={loading}
        />
      </div>

      {/* Daily steam consumption */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Steam Consumption Trend</h3>
        <p className="text-xs text-gray-500 mb-2">Daily tonnes</p>
        {(data?.daily_trend || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.daily_trend}>
              <defs>
                <linearGradient id="steamGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.5}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}/>
              <YAxis tick={{ fontSize: 10 }}/>
              <Tooltip formatter={(v, n) => [`${fmt(v, 2)} ${n === 'steam_tonnes' ? 'T' : 'kWh'}`, n === 'steam_tonnes' ? 'Tonnes' : 'kWh-eq']}/>
              <Area type="monotone" dataKey="steam_tonnes" stroke="#f59e0b" fill="url(#steamGrad)" strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
        ) : <Empty hint="No steam data yet — upload energy CSV with energy_type='steam'"/>}
      </div>

      {/* Boiler efficiency trend */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Boiler Efficiency Trend (proxy)</h3>
        <p className="text-xs text-gray-500 mb-2">Daily ratio of steam_kWh ÷ fuel_kWh — typical industrial boilers run 70-85%.</p>
        {(data?.efficiency_trend || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.efficiency_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}/>
              <YAxis tick={{ fontSize: 10 }} domain={[0, 'auto']} tickFormatter={v => `${v}%`}/>
              <Tooltip formatter={v => v != null ? `${fmt(v, 1)}%` : '—'}/>
              <ReferenceLine y={75} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'Target 75%', fontSize: 10, fill: '#10b981' }}/>
              <Line type="monotone" dataKey="efficiency_pct" stroke="#059669" strokeWidth={2} dot={{ r: 2 }}/>
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty hint="Need both steam + boiler-fuel rows to compute efficiency"/>}
      </div>

      {/* Steam SEU trend */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Steam SEU Trend</h3>
        <p className="text-xs text-gray-500 mb-2">Tonnes of steam per unit of production — lower is better.</p>
        {(data?.seu_trend || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.seu_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}/>
              <YAxis tick={{ fontSize: 10 }}/>
              <Tooltip formatter={v => v != null ? `${fmt(v, 4)} T/${k.production_unit || 'unit'}` : '—'}/>
              <Line type="monotone" dataKey="steam_per_unit" stroke="#7c3aed" strokeWidth={2} dot={{ r: 2 }}/>
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty hint="Need both steam + production rows to compute SEU"/>}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent = 'gray', loading }) {
  const accents = {
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   iconBg: 'bg-amber-100',   iconText: 'text-amber-600' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    iconBg: 'bg-blue-100',    iconText: 'text-blue-600' },
    rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    iconBg: 'bg-rose-100',    iconText: 'text-rose-600' },
    gray:    { bg: 'bg-gray-50',    text: 'text-gray-700',    iconBg: 'bg-gray-100',    iconText: 'text-gray-600' },
  };
  const a = accents[accent] || accents.gray;
  if (loading) return (
    <div className={`p-3 rounded-lg border border-gray-200 ${a.bg} animate-pulse`}>
      <div className="h-3 w-20 bg-gray-200 rounded mb-2"/>
      <div className="h-6 w-28 bg-gray-200 rounded"/>
    </div>
  );
  return (
    <div className={`p-3 rounded-lg border border-gray-200 ${a.bg}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wide ${a.text}`}>{label}</div>
      <div className="text-xl font-bold text-gray-900 mt-1 truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Empty({ hint }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-gray-400 py-8 text-xs">
      <BarChart3 className="w-8 h-8 text-gray-300 mb-2"/>
      <div>{hint}</div>
    </div>
  );
}
